/**
 * FunnelExperience
 *
 * Orchestrator component that evaluates the funnel step for a given page type
 * and renders the correct UI component (modal, banner, or popup).
 *
 * - onAccept (CTA button) = call accept API + add-to-cart, then dismiss
 * - onDismiss (X button) = temporary hide, reappears on next page visit
 * - onDecline ("No thanks") = call decline API, suppress for the rest of the session
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import styled from '@emotion/styled';
import { useFunnelExperience } from '../use-funnel-experience';
import type { FunnelPageType } from '../use-funnel-experience';
import { useFunnelContext } from '../funnel-provider';
import { extractBannerPlacement, resolveBannerContainerStyle } from '../banner-placement';
import { useFunnelAnalytics } from '../analytics';
import type {
  PageContext,
  StepPageContent,
  StepPageBlock,
  FunnelOffer,
  DisplaySettings,
  StepExperience,
  FunnelAddToCartCallback,
} from '../types';
import { FunnelModal } from './FunnelModal';
import { FunnelBanner } from './FunnelBanner';
import { FunnelPopup } from './FunnelPopup';
import { FunnelToast } from './FunnelToast';
import { FunnelPageContent } from './FunnelPageContent';
import { ActionExecutor } from './ActionExecutor';
import { ListenerBinder } from './ListenerBinder';
import { injectStepProductIds } from './injectStepProductIds';
import { funnelLogger } from '../logger';
import { isFunnelSecureCartProofEnabled, useBehavioralTracker } from '../events';
import type { StepListener } from '../types';
import { StepReactions } from './StepReactions';
import type { SemanticResultOptions } from '../handlers/types';
import { useStepTimeoutEmitter } from '../use-funnel-step-emitters';
import { useFunnelStepEmitters } from '../use-funnel-step-emitters';
import { EVENT_TYPES, publishBrowserBusinessEvent } from '@template-project/storefront-shared';
import { isStepCompatibleWithPage } from '../step-page-compatibility';
import { resolveFunnelOfferPricing } from '../offer-pricing';
import { resolveCartActionOffer } from '../resolve-cart-action-offer';

type TemplateLoadState = 'idle' | 'loading' | 'ready' | 'failed';

interface FunnelExperienceProps {
  pageType: FunnelPageType;
  context: Omit<PageContext, 'pageType'>;
  onAddToCart?: FunnelAddToCartCallback;
  /** Reserves the exact cart UUID before funnels signs an accepted offer. */
  prepareCartToken?: () => string | Promise<string>;
  /**
   * Storefront PDP routes already render the product page. When set, an authored
   * `experienceType: "page"` step keeps listeners/actions active but does not
   * mount the fullscreen takeover that would block the host PDP.
   */
  suppressPageTakeover?: boolean;
}

const SelectorWrap = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 0 0 12px;
`;

const SelectorButton = styled.button<{ $selected: boolean }>`
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.text.primary : theme.colors.border.color)};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.text.primary : theme.colors.background.primary};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.background.primary : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export function FunnelExperience({
  pageType,
  context,
  onAddToCart,
  prepareCartToken,
  suppressPageTakeover = false,
}: FunnelExperienceProps) {
  const {
    loading,
    experience,
    offer,
    experienceType,
    activeFunnelId,
    dismiss,
    decline,
    overrideExperience,
  } = useFunnelExperience(pageType, context);
  const {
    acceptOffer,
    declineOffer,
    navigateToStep,
    assignedFunnelId,
    fetchTemplate,
    setActiveExperience,
  } = useFunnelContext();
  const analytics = useFunnelAnalytics();
  const { emitBehavioralEvent } = useFunnelStepEmitters();
  const funnelId = assignedFunnelId ?? activeFunnelId ?? '';
  const [accepting, setAccepting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const secureCartProofEnabled = isFunnelSecureCartProofEnabled();
  // WP-1: honor step-level timeoutSeconds — a visible step that is ignored for
  // its configured duration fires the `timeout` transition (no dead wire).
  useStepTimeoutEmitter(experience?.id, experience?.timeoutSeconds);

  const getActionErrorMessage = useCallback((error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }, []);

  // ADR 0004 — DOM-level behavioral event emitter (scroll depth + exit intent)
  useBehavioralTracker(experience?.id);

  useEffect(() => {
    setActionError(null);
  }, [experience?.id]);

  useEffect(() => {
    setSelectedOfferId(experience?.offers?.[0]?.id ?? experience?.offer?.id ?? null);
  }, [experience?.id, experience?.offers, experience?.offer?.id]);

  const activeOffer =
    (selectedOfferId
      ? experience?.offers?.find((item) => item.id === selectedOfferId) ?? null
      : null) ??
    experience?.offers?.[0] ??
    offer ??
    null;

  const emitOfferAccepted = useCallback(
    (acceptedOffer: FunnelOffer) => {
      const pricing = resolveFunnelOfferPricing(acceptedOffer);
      const eventId = publishBrowserBusinessEvent(EVENT_TYPES.OFFER_ACCEPTED, {
        funnel_id: funnelId,
        offer_id: acceptedOffer.id,
        offer_name: acceptedOffer.name,
        step_id: experience?.id ?? '',
        discount_type: pricing.discountType,
        discount_value: pricing.discountValue,
        original_price_cents: pricing.originalPriceCents,
        final_price_cents: pricing.finalPriceCents,
      });
      emitBehavioralEvent({
        event: 'offer_accepted',
        phase: 'result',
        origin: 'domain_authority',
        businessEventId: eventId,
        params: {
          offerId: acceptedOffer.id,
          offerName: acceptedOffer.name,
          productId: acceptedOffer.productId,
          variantId: acceptedOffer.variantId,
          runtimeNavigationHandled: true,
        },
        pageContext: { pageType },
      });
    },
    [emitBehavioralEvent, experience?.id, funnelId, pageType]
  );

  const emitOfferDeclined = useCallback(
    (declinedOffer: FunnelOffer) => {
      const eventId = publishBrowserBusinessEvent(EVENT_TYPES.OFFER_DECLINED, {
        funnel_id: funnelId,
        offer_id: declinedOffer.id,
        offer_name: declinedOffer.name,
        step_id: experience?.id ?? '',
      });
      emitBehavioralEvent({
        event: 'offer_declined',
        phase: 'result',
        origin: 'domain_authority',
        businessEventId: eventId,
        params: {
          offerId: declinedOffer.id,
          offerName: declinedOffer.name,
          runtimeNavigationHandled: true,
        },
        pageContext: { pageType },
      });
    },
    [emitBehavioralEvent, experience?.id, funnelId, pageType]
  );

  const handleSelectOffer = useCallback(
    (offerId: string) => {
      setSelectedOfferId(offerId);
      const selected = experience?.offers?.find((item) => item.id === offerId);
      emitBehavioralEvent({
        event: 'variant_selected',
        phase: 'result',
        origin: 'handler',
        params: {
          offerId,
          productId: selected?.productId,
          variantId: selected?.variantId,
        },
        pageContext: { pageType },
      });
    },
    [emitBehavioralEvent, experience?.offers, pageType]
  );

  const applyNextStep = useCallback(
    (nextStep: StepExperience) => {
      if (isStepCompatibleWithPage(nextStep, pageType)) {
        overrideExperience(nextStep);
        return;
      }

      // The server has advanced the journey, so persist the returned step id for
      // continuity. Its visual belongs to another route and must remain hidden
      // until that route performs the next page-context evaluation.
      overrideExperience(nextStep, { visible: false });
    },
    [overrideExperience, pageType]
  );

  const acceptAndAddCartOffer = useCallback(
    async (
      offerId: string,
      productId: string,
      variantId: string | undefined,
      quantity = 1,
      item?: Parameters<FunnelAddToCartCallback>[3]
    ) => {
      if (!secureCartProofEnabled) {
        throw new Error('Secure funnel cart pricing is not active');
      }

      if (!experience) {
        return {
          navigation: { complete: true, nextStep: null },
          actionResult: undefined,
        };
      }

      if (!onAddToCart) {
        return {
          navigation: await acceptOffer({
            stepId: experience.id,
            offerId,
            productId,
            variantId,
            quantity,
            action: 'add_to_cart',
          }),
          actionResult: undefined,
        };
      }
      if (!prepareCartToken) {
        throw new Error('Funnel cart integration is missing cart token preparation');
      }

      const cartToken = await prepareCartToken();
      const nav = await acceptOffer({
        stepId: experience.id,
        offerId,
        productId,
        variantId,
        quantity,
        action: 'add_to_cart',
        cartToken,
      });
      if (!nav.acceptedOfferToken) {
        throw new Error('Accepted funnel offer is missing cart pricing authorization');
      }

      const actionResult = await onAddToCart(productId, variantId ?? '', quantity, item, {
        cartToken,
        funnelOfferToken: nav.acceptedOfferToken,
      });
      analytics.trackAddToCart({
        productId,
        variantId: variantId ?? '',
        quantity,
        source: 'funnel_offer',
      });
      return { navigation: nav, actionResult };
    },
    [acceptOffer, analytics, experience, onAddToCart, prepareCartToken, secureCartProofEnabled]
  );

  const handleAuthorizedActionAddToCart = useCallback<FunnelAddToCartCallback>(
    async (productId, variantId, quantity, item) => {
      if (!experience || !onAddToCart || !prepareCartToken) {
        throw new Error('Funnel cart integration is not available for this action');
      }

      const actionOffer = resolveCartActionOffer(
        experience.offers,
        activeOffer,
        productId,
        variantId
      );
      if (!actionOffer?.productId || !actionOffer.variantId) {
        throw new Error('Funnel add_to_cart action does not match exactly one eligible step offer');
      }

      setActionError(null);
      try {
        const { navigation, actionResult } = await acceptAndAddCartOffer(
          actionOffer.id,
          actionOffer.productId,
          actionOffer.variantId,
          quantity,
          item
        );
        emitOfferAccepted(actionOffer);
        if (navigation.nextStep) {
          applyNextStep(navigation.nextStep);
        }
        return actionResult;
      } catch (error) {
        funnelLogger.error(
          '[FunnelExperience] add_to_cart action authorization failed',
          error instanceof Error ? error : undefined,
          { stepId: experience.id, funnelId, productId, variantId, quantity }
        );
        setActionError(
          getActionErrorMessage(error, 'We could not add this offer right now. Please try again.')
        );
        throw error;
      }
    },
    [
      acceptAndAddCartOffer,
      activeOffer,
      applyNextStep,
      emitOfferAccepted,
      experience,
      funnelId,
      getActionErrorMessage,
      onAddToCart,
      prepareCartToken,
    ]
  );

  // Fetch step-level template content + displaySettings when templateId is set
  const [templateContent, setTemplateContent] = useState<StepPageBlock[] | null>(null);
  const [templateDisplaySettings, setTemplateDisplaySettings] = useState<DisplaySettings | null>(
    null
  );
  const [templateLoadState, setTemplateLoadState] = useState<TemplateLoadState>('idle');
  useEffect(() => {
    if (!experience?.templateId) {
      setTemplateContent(null);
      setTemplateDisplaySettings(null);
      setTemplateLoadState('idle');
      return;
    }
    // TF-AUD-005: Clear stale template content immediately when templateId
    // changes, so we don't briefly render the previous template's content.
    setTemplateContent(null);
    setTemplateDisplaySettings(null);
    setTemplateLoadState('loading');
    let cancelled = false;
    fetchTemplate(experience.templateId)
      .then((data) => {
        if (!cancelled) {
          if (data?.pageContent) {
            // pageContent from the template may be StepPageContent (with .blocks) or StepPageBlock[]
            const blocks = Array.isArray(data.pageContent)
              ? data.pageContent
              : (data.pageContent as StepPageContent).blocks ?? null;
            setTemplateContent(blocks);
            setTemplateLoadState('ready');
          } else {
            setTemplateLoadState('failed');
          }
          if (data?.displaySettings) {
            setTemplateDisplaySettings(data.displaySettings ?? null);
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplateLoadState('failed');
        funnelLogger.warn('FunnelExperience template fetch failed; using pageContent fallback', {
          templateId: experience.templateId ?? null,
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [experience?.templateId, fetchTemplate]);

  const handleAccept = useCallback(async () => {
    if (!experience) return false;
    setAccepting(true);
    setActionError(null);
    try {
      if (activeOffer) {
        if (!activeOffer.productId || !activeOffer.variantId) {
          setAccepting(false);
          setActionError(
            'This promotion is missing product or variant information. Please contact support.'
          );
          return false;
        }
        const { navigation } = await acceptAndAddCartOffer(
          activeOffer.id,
          activeOffer.productId,
          activeOffer.variantId
        );
        emitOfferAccepted(activeOffer);
        setAccepting(false);
        if (navigation.nextStep) {
          applyNextStep(navigation.nextStep);
          return true;
        }
      }
      // Success — dismiss the experience
      setAccepting(false);
      dismiss();
      return true;
    } catch (err) {
      funnelLogger.error(
        '[FunnelExperience] accept failed',
        err instanceof Error ? err : undefined,
        {
          stepId: experience.id,
          funnelId,
        }
      );
      setAccepting(false);
      setActionError(
        getActionErrorMessage(
          err,
          'We could not process this purchase right now. Please try again.'
        )
      );
      return false;
    }
  }, [
    experience,
    activeOffer,
    acceptAndAddCartOffer,
    dismiss,
    funnelId,
    getActionErrorMessage,
    emitOfferAccepted,
    applyNextStep,
  ]);

  const handleDecline = useCallback(async () => {
    if (!experience) {
      decline();
      return true;
    }
    setActionError(null);
    try {
      // Call decline API (records event, returns downsell step if any)
      const nav = await declineOffer(experience.id);
      if (activeOffer) emitOfferDeclined(activeOffer);

      if (nav.nextStep) {
        // Same-page downsells render immediately. Cross-page transitions keep
        // their server-authoritative step id but wait for the destination page.
        applyNextStep(nav.nextStep);
        return true;
      }
    } catch (err) {
      funnelLogger.error(
        '[FunnelExperience] decline failed',
        err instanceof Error ? err : undefined,
        {
          stepId: experience.id,
          funnelId,
        }
      );
      setActionError(
        getActionErrorMessage(
          err,
          'We could not process your response right now. Please try again.'
        )
      );
      return false;
    }
    decline();
    return true;
  }, [
    experience,
    declineOffer,
    decline,
    analytics,
    funnelId,
    activeOffer,
    getActionErrorMessage,
    emitOfferDeclined,
    applyNextStep,
  ]);

  // D-C: server-validated go_to_step. The client no longer refuses the jump —
  // it asks funnels-go, which authorizes it only when the current step authored
  // a go_to_step reaction to the target, then swaps in the returned experience
  // (mirroring the decline downsell-chaining path).
  const handleGoToStep = useCallback(
    async (targetStepId: string | undefined) => {
      if (!experience || !targetStepId) return false;
      setActionError(null);
      try {
        const nav = await navigateToStep(experience.id, targetStepId);
        if (nav.nextStep) {
          applyNextStep(nav.nextStep);
          return true;
        }
        // Server accepted but returned no experience (terminal target) — dismiss.
        dismiss();
        return true;
      } catch (err) {
        funnelLogger.warn(
          'Step listener go_to_step navigation refused by server; staying on current step',
          {
            stepId: experience.id,
            funnelId,
            targetStepId,
            error: err instanceof Error ? err.message : String(err),
          }
        );
        setActionError(
          getActionErrorMessage(err, 'We could not navigate right now. Please try again.')
        );
        return false;
      }
    },
    [experience, navigateToStep, dismiss, funnelId, getActionErrorMessage, applyNextStep]
  );

  const handleDismiss = useCallback(() => {
    if (experience) {
      analytics.trackExperienceDismissed({ funnelId, stepId: experience.id });
    }
    dismiss();
  }, [dismiss, experience, funnelId, analytics]);

  // display_offer accept/decline — shared by the step's ActionExecutor and by
  // listener-reaction ActionExecutors (ADR 0004 R16) so both drive the same
  // server-validated offer flow.
  const handleActionOfferAccept = useCallback(
    async (offerId: string, options?: SemanticResultOptions) => {
      if (!experience) return false;
      setActionError(null);
      try {
        const resolvedActionOffer =
          activeOffer?.id === offerId
            ? activeOffer
            : experience.offers?.find((item) => item.id === offerId) ?? activeOffer ?? null;
        if (!resolvedActionOffer?.productId || !resolvedActionOffer.variantId) {
          throw new Error('This promotion is missing product or variant information.');
        }
        const { navigation } = await acceptAndAddCartOffer(
          resolvedActionOffer.id,
          resolvedActionOffer.productId,
          resolvedActionOffer.variantId
        );
        if (options?.resultOwner !== 'dispatcher') {
          emitOfferAccepted(resolvedActionOffer);
        }
        if (navigation.nextStep) {
          applyNextStep(navigation.nextStep);
        } else {
          dismiss();
        }
        return true;
      } catch (err) {
        funnelLogger.error(
          '[FunnelExperience] display_offer accept failed',
          err instanceof Error ? err : undefined,
          {
            stepId: experience.id,
            funnelId,
          }
        );
        const message = getActionErrorMessage(
          err,
          'We could not process this purchase right now. Please try again.'
        );
        setActionError(message);
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [
      experience,
      activeOffer,
      acceptAndAddCartOffer,
      funnelId,
      getActionErrorMessage,
      emitOfferAccepted,
      applyNextStep,
      dismiss,
    ]
  );

  const handleActionOfferDecline = useCallback(
    async (_offerId: string, options?: SemanticResultOptions) => {
      if (!experience) return false;
      setActionError(null);
      try {
        const nav = await declineOffer(experience.id);
        if (activeOffer && options?.resultOwner !== 'dispatcher') {
          emitOfferDeclined(activeOffer);
        }
        if (nav.nextStep) {
          applyNextStep(nav.nextStep);
        } else {
          decline();
        }
        return true;
      } catch (err) {
        funnelLogger.error(
          '[FunnelExperience] display_offer decline failed',
          err instanceof Error ? err : undefined,
          {
            stepId: experience.id,
            funnelId,
          }
        );
        const message = getActionErrorMessage(
          err,
          'We could not process your response right now. Please try again.'
        );
        setActionError(message);
        throw err instanceof Error ? err : new Error(message);
      }
    },
    [
      experience,
      declineOffer,
      funnelId,
      getActionErrorMessage,
      activeOffer,
      emitOfferDeclined,
      applyNextStep,
      decline,
    ]
  );

  // Track experience shown (fire once per step)
  const trackedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!experience || trackedStepRef.current === experience.id) return;
    trackedStepRef.current = experience.id;
    analytics.trackExperienceShown({
      funnelId,
      stepId: experience.id,
      stepName: experience.name,
      experienceType: experienceType ?? undefined,
      placement: experience.placement ?? undefined,
    });
  }, [experience, experienceType, funnelId, analytics]);

  // TF-AUD-005: When templateId is set, template content ALWAYS wins (live
  // templates). pageContent is preserved as fallback for when template fetch
  // fails or templateId is cleared.
  const effectivePageContent: StepPageContent | StepPageBlock[] | null = experience?.templateId
    ? templateContent ?? experience?.pageContent ?? null
    : experience?.pageContent ?? templateContent;

  // Populate template product-showcase blocks with the products chosen on the
  // step (distributed across showcase blocks by their column count).
  const renderedPageContent = useMemo(
    () => injectStepProductIds(effectivePageContent, experience?.productIds),
    [effectivePageContent, experience?.productIds]
  );

  // Nothing to show — render if we have an offer OR page content with blocks
  const hasPageContent = (() => {
    if (!effectivePageContent) return false;
    if (Array.isArray(effectivePageContent)) return effectivePageContent.length > 0;
    return (
      (Array.isArray(effectivePageContent.blocks) && effectivePageContent.blocks.length > 0) ||
      (Array.isArray(effectivePageContent.components) && effectivePageContent.components.length > 0)
    );
  })();

  // TF-209: Extract actions early to check if we should render even without offer/pageContent
  const stepActions = experience?.actions;
  const hasActions = stepActions && stepActions.length > 0;

  // TF-210: Extract listeners early so the guard can account for 'none' type with listeners
  const stepListeners = experience?.listeners;
  const hasListeners = stepListeners && stepListeners.length > 0;
  const triggerLifecycle = experience?.triggerLifecycle ?? 'session';

  useEffect(() => {
    if (
      !experience?.templateId ||
      templateLoadState === 'idle' ||
      templateLoadState === 'loading'
    ) {
      return;
    }

    if (templateLoadState === 'ready') {
      return;
    }

    const fallbackMode = hasPageContent
      ? 'step_page_content'
      : activeOffer
        ? 'hardcoded_offer_layout'
        : hasActions || hasListeners
          ? 'action_only'
          : 'nothing_renderable';

    funnelLogger.warn('FunnelExperience template fallback activated', {
      stepId: experience.id,
      funnelId,
      templateId: experience.templateId,
      reason: 'template_fetch_failed_or_empty',
      fallbackMode,
      experienceType: experienceType ?? null,
      hasStepPageContent: Boolean(experience.pageContent),
      hasOffer: Boolean(activeOffer),
      hasActions: Boolean(hasActions),
      hasListeners: Boolean(hasListeners),
    });
  }, [
    experience?.id,
    experience?.templateId,
    experience?.pageContent,
    funnelId,
    templateLoadState,
    hasPageContent,
    activeOffer,
    hasActions,
    hasListeners,
    experienceType,
  ]);

  // Log which rendering case is active on mount (dev only, section 16 of step-template-relationship.md)
  useEffect(() => {
    if (!experience) return;
    const templateId = experience.templateId;
    const hasPC = Boolean(hasPageContent);
    const actCount = stepActions?.length ?? 0;
    const renderCase =
      templateId && hasPC ? 'C' : templateId ? 'A' : hasPC ? 'B' : actCount > 0 ? 'D' : 'E';
    if (process.env.NODE_ENV === 'development') {
      funnelLogger.info('FunnelExperience render case', {
        stepId: experience.id,
        renderCase,
        templateId: templateId ?? null,
        pageContent: hasPC,
        actions: actCount,
        experienceType: experienceType ?? null,
      });
    }
  }, [experience, hasPageContent, stepActions, experienceType]);

  const isTemplatePending =
    Boolean(experience?.templateId) && !templateContent && templateLoadState !== 'failed';

  // Keep hook ordering stable across template loading/runtime transitions.
  // The effect itself decides whether there is an active visible experience.
  const displaySettings = experience?.displaySettings ?? templateDisplaySettings ?? undefined;
  const bannerPlacement =
    experienceType === 'banner' ? extractBannerPlacement(displaySettings) : null;
  // CartPage mounts FunnelExperience in-flow above native cart UI. Fixed
  // top/bottom placement would overlay line items (multi-block cart banners).
  const bannerContainerStyle =
    experienceType === 'banner'
      ? (resolveBannerContainerStyle(bannerPlacement, {
          inlineHost: pageType === 'cart',
        }) as CSSProperties | undefined)
      : undefined;
  const experienceOffers = experience?.offers;
  const multiOffers =
    Array.isArray(experienceOffers) && experienceOffers.length > 1 ? experienceOffers : null;

  useEffect(() => {
    if (
      loading ||
      isTemplatePending ||
      !experience ||
      !experienceType ||
      (!activeOffer && !hasPageContent && !hasActions && !hasListeners)
    ) {
      setActiveExperience(null);
      return;
    }

    setActiveExperience({
      funnelId,
      stepId: experience.id,
      experienceType,
      placement: experience.placement,
      displaySettings,
      bannerPlacement,
    });

    return () => {
      setActiveExperience(null);
    };
  }, [
    loading,
    isTemplatePending,
    experience,
    experienceType,
    funnelId,
    activeOffer,
    hasPageContent,
    hasActions,
    hasListeners,
    displaySettings,
    bannerPlacement,
    setActiveExperience,
  ]);

  if (isTemplatePending) {
    if (experienceType === 'page' && !suppressPageTakeover) {
      return <div aria-live="polite">Loading page...</div>;
    }
    return null;
  }

  if (loading || !experience || (!activeOffer && !hasPageContent && !hasActions && !hasListeners)) {
    return null;
  }

  // Provide a safe fallback offer for page-content-only experiences
  const safeOffer: FunnelOffer = activeOffer ?? {
    id: '',
    name: '',
    productId: '',
    variantId: '',
    headline: '',
    description: '',
    imageUrl: '',
    ctaText: 'Shop Now',
    declineText: 'No thanks',
    pricingType: 'fixed',
    pricingValue: null,
    originalPrice: null,
  };

  // TF-AUD-004: frequencyCap controls experience visibility (step-level),
  // triggerLifecycle controls action/listener firing (funnel-level). They are
  // separate concerns with incompatible value sets — never conflate them.

  let experienceElement: React.ReactNode = null;

  switch (experienceType) {
    case 'modal':
      experienceElement = (
        <FunnelModal
          offer={safeOffer}
          pageContent={renderedPageContent}
          displaySettings={displaySettings}
          errorMessage={actionError}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
          suppressOfferActions={!secureCartProofEnabled}
        />
      );
      break;
    case 'banner':
      experienceElement = (
        <FunnelBanner
          offer={safeOffer}
          pageContent={renderedPageContent}
          displaySettings={displaySettings}
          containerStyle={bannerContainerStyle}
          errorMessage={actionError}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
          suppressOfferActions={!secureCartProofEnabled}
        />
      );
      break;
    case 'popup':
      experienceElement = (
        <FunnelPopup
          offer={safeOffer}
          pageContent={renderedPageContent}
          displaySettings={displaySettings}
          errorMessage={actionError}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
          suppressOfferActions={!secureCartProofEnabled}
        />
      );
      break;
    case 'toast':
      experienceElement = (
        <FunnelToast
          offer={safeOffer}
          pageContent={renderedPageContent}
          displaySettings={displaySettings}
        />
      );
      break;
    case 'page':
      experienceElement = suppressPageTakeover ? null : (
        <FunnelPageContent
          offer={safeOffer}
          offers={experience.offers}
          selectedOfferId={selectedOfferId ?? undefined}
          onSelectOffer={handleSelectOffer}
          pageContent={renderedPageContent}
          displaySettings={displaySettings}
          errorMessage={actionError}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onDismiss={handleDismiss}
          accepting={accepting}
          layoutMode={experience.layoutMode ?? 'card'}
          suppressOfferActions={!secureCartProofEnabled}
          // Page experience owns the viewport (full_width / card). Native cart
          // chrome underneath stays mounted for listeners but is covered.
          takeoverOverlay
        />
      );
      break;
    case 'none':
      // No visual experience — only actions and listeners run
      experienceElement = null;
      break;
    default:
      experienceElement = null;
      break;
  }

  return (
    <>
      {multiOffers &&
        experienceType !== 'page' &&
        experienceType !== 'toast' &&
        experienceType !== 'none' && (
        <SelectorWrap aria-label="Offer selector">
          {multiOffers.map((item, index) => {
            const isSelected =
              (selectedOfferId ?? safeOffer.id) === item.id || (!selectedOfferId && index === 0);
            return (
              <SelectorButton
                key={item.id}
                type="button"
                $selected={isSelected}
                aria-pressed={isSelected}
                onClick={() => handleSelectOffer(item.id)}
              >
                {item.name || item.headline || `Offer ${index + 1}`}
              </SelectorButton>
            );
          })}
        </SelectorWrap>
      )}
      {experienceElement}
      {stepActions && stepActions.length > 0 && (
        <ActionExecutor
          actions={stepActions}
          stepId={experience.id}
          triggerLifecycle={triggerLifecycle}
          onAddToCart={
            secureCartProofEnabled && onAddToCart ? handleAuthorizedActionAddToCart : undefined
          }
          fallbackOffer={activeOffer ?? undefined}
          onOfferAccept={secureCartProofEnabled ? handleActionOfferAccept : undefined}
          onOfferDecline={handleActionOfferDecline}
          commerceEnabled={secureCartProofEnabled}
        />
      )}
      <StepReactions
        stepId={experience.id}
        listeners={stepListeners as StepListener[] | undefined}
        triggerLifecycle={triggerLifecycle}
        onAccept={handleAccept}
        onDecline={handleDecline}
        onGoToStep={(targetStepId) => {
          return handleGoToStep(targetStepId);
        }}
        onAddToCart={
          secureCartProofEnabled && onAddToCart ? handleAuthorizedActionAddToCart : undefined
        }
        fallbackOffer={activeOffer ?? undefined}
        onOfferAccept={secureCartProofEnabled ? handleActionOfferAccept : undefined}
        onOfferDecline={handleActionOfferDecline}
        commerceEnabled={secureCartProofEnabled}
      />
      {stepListeners && stepListeners.length > 0 && (
        <ListenerBinder
          listeners={stepListeners}
          stepId={experience.id}
          triggerLifecycle={triggerLifecycle}
        />
      )}
    </>
  );
}
